/* Storefront filtering and sorting for the Royalty sidebar (collection-search-cart.md §3.2 and §6.7).
   Dawn's approach, trimmed to the sidebar markup: the section is re-rendered through the Section Rendering API,
   responses are cached per URL, and the address bar follows with pushState / popstate. Ticking a value or
   pressing Apply (or Enter) applies at once; the current sort is always kept. */

class FacetFiltersForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form.addEventListener('change', (event) => {
      if (event.target.type === 'checkbox') this.apply();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply();
    });
    // Enter on a checkbox ticks it and applies, like Space (decisions.md #4).
    this.form.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.target.type !== 'checkbox') return;
      event.preventDefault();
      event.target.click();
    });
  }

  apply() {
    FacetFiltersForm.renderPage(FacetFiltersForm.searchParams(this.form));
  }

  static searchParams(form) {
    const params = new URLSearchParams(form ? new FormData(form) : window.location.search);
    const sort = document.querySelector('[data-sort-by]');
    if (sort) params.set('sort_by', sort.value);
    params.delete('page');
    [...params.keys()].forEach((key) => {
      if (params.get(key) === '') params.delete(key);
    });
    return params.toString();
  }

  static sectionElement() {
    const id = document.getElementById('ProductGridContainer')?.dataset.id;
    return id ? { id, element: document.getElementById(`shopify-section-${id}`) } : null;
  }

  static async renderPage(searchParams, pushState = true) {
    const generation = ++FacetFiltersForm.generation;
    const section = FacetFiltersForm.sectionElement();
    if (!section?.element) {
      window.location.search = searchParams;
      return;
    }
    const url = `${window.location.pathname}?section_id=${section.id}&${searchParams}`;
    document.getElementById('ProductGridContainer')?.classList.add('loading');
    if (pushState) history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);

    try {
      let html = FacetFiltersForm.cache.get(url);
      if (!html) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        html = await response.text();
        FacetFiltersForm.cache.set(url, html);
      }
      if (generation !== FacetFiltersForm.generation) return;
      FacetFiltersForm.renderSection(section.element, html);
    } catch (error) {
      if (generation !== FacetFiltersForm.generation) return;
      console.error(error);
      window.location.search = searchParams;
    }
  }

  static renderSection(element, html) {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (!source) throw new Error('Section missing from response');
    const focusedId = document.activeElement?.id;
    const openIds = [...element.querySelectorAll('details[id][open]')].map((details) => details.id);
    const closedIds = [...element.querySelectorAll('details[id]:not([open])')].map((details) => details.id);

    const sectionId = element.id.replace('shopify-section-', '');
    document.querySelectorAll('modal-dialog[data-section], quick-add-modal[data-section]').forEach((modal) => {
      if (modal.dataset.section !== sectionId) return;
      if (modal.hasAttribute('open')) modal.hide();
      modal.remove();
    });
    element.innerHTML = source.innerHTML;

    openIds.forEach((id) => document.getElementById(id)?.setAttribute('open', ''));
    closedIds.forEach((id) => document.getElementById(id)?.removeAttribute('open'));
    if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
  }
}

FacetFiltersForm.cache = new Map();
FacetFiltersForm.generation = 0;
customElements.define('facet-filters-form', FacetFiltersForm);

window.addEventListener('popstate', (event) => {
  if (!document.querySelector('facet-filters-form, [data-sort-by]')) return;
  FacetFiltersForm.renderPage(event.state?.searchParams ?? window.location.search.slice(1), false);
});

document.addEventListener('change', (event) => {
  if (!event.target.matches('[data-sort-by]')) return;
  const form = document.querySelector('facet-filters-form form');
  if (form) {
    FacetFiltersForm.renderPage(FacetFiltersForm.searchParams(form));
    return;
  }
  const params = new URLSearchParams(window.location.search);
  params.set('sort_by', event.target.value);
  params.delete('page');
  window.location.search = params.toString();
});

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-facet-link]');
  if (!link || event.metaKey || event.ctrlKey) return;
  event.preventDefault();
  FacetFiltersForm.renderPage(new URL(link.href).search.slice(1));
});

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('input').forEach((input) =>
      input.addEventListener('change', () => {
        const value = Number(input.value);
        const max = Number(input.max);
        if (input.value !== '' && value < 0) input.value = 0;
        if (max && value > max) input.value = max;
      })
    );
  }
}

customElements.define('price-range', PriceRange);
